import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ShieldCheckIcon, ExclamationTriangleIcon, CameraIcon } from '@heroicons/react/24/outline';
import { useCandidateT } from '../../i18n/candidate';

const EVENT_THROTTLE_MS = 2500; // Max one event POST per type per 2.5s to avoid ERR_INSUFFICIENT_RESOURCES

const ProctoringMonitor = ({
  sessionId,
  config,
  onViolation,
  onScreenshotTaken,
  showUI = true,
  suppressToasts = false,
}) => {
  const t = useCandidateT();
  const [isActive, setIsActive] = useState(false);
  const lastEventSentByType = useRef({});
  const [violations, setViolations] = useState({
    fullscreen_exit: 0,
    tab_switch: 0,
    copy_paste: 0,
    mouse_leave: 0,
  });
  const violationsRef = useRef(violations);
  useEffect(() => {
    violationsRef.current = violations;
  }, [violations]);

  const [isFullScreen, setIsFullScreen] = useState(false);
  const screenshotIntervalRef = useRef(null);
  const [cameraStream, setCameraStream] = useState(null);
  const listenersRef = useRef([]);
  const videoRef = useRef(null);
  const cameraIntervalRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);

  const initializeMonitoring = useCallback(async () => {
    try {
      // Start monitoring based on configuration
      if (config.fullScreen?.enabled) {
        startFullScreenMonitoring();
      }

      if (config.tabProctoring?.enabled) {
        startTabMonitoring();
      }

      if (config.screenshotProctoring?.enabled) {
        startScreenshotMonitoring();
      }

      if (config.copyPastePrevention?.enabled) {
        startCopyPasteMonitoring();
      }

      if (config.mouseMonitoring?.enabled) {
        startMouseMonitoring();
      }



      setIsActive(true);
    } catch (error) {
      if (!suppressToasts) {
        toast.error(t('Failed to initialize monitoring system'));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, suppressToasts]);

  const startFullScreenMonitoring = () => {
    const handleFullScreenChange = () => {
      const isCurrentlyFullScreen = document.fullscreenElement !== null;
      setIsFullScreen(isCurrentlyFullScreen);

      if (!isCurrentlyFullScreen && config.fullScreen.forceFullScreen) {
        // Force back to full screen if enabled
        document.documentElement.requestFullscreen().catch(() => {});
      }

      if (!isCurrentlyFullScreen) {
        recordViolation('fullscreen_exit', 'medium', 'Candidate exited full screen mode');
      }
    };

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    listenersRef.current.push({ target: document, event: 'fullscreenchange', handler: handleFullScreenChange, capture: false });

    // Request full screen if required
    if (config.fullScreen.forceFullScreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  };

  const startTabMonitoring = () => {
    let hidden = false;
    let visibilityChange = null;

    if (typeof document.hidden !== 'undefined') {
      hidden = 'hidden';
      visibilityChange = 'visibilitychange';
    } else if (typeof document.msHidden !== 'undefined') {
      hidden = 'msHidden';
      visibilityChange = 'msvisibilitychange';
    } else if (typeof document.webkitHidden !== 'undefined') {
      hidden = 'webkitHidden';
      visibilityChange = 'webkitvisibilitychange';
    }

    const handleVisibilityChange = () => {
      if (document[hidden]) {
        recordViolation('tab_switch', 'medium', 'Candidate switched tabs or minimized window');
      }
    };

    if (visibilityChange) {
      document.addEventListener(visibilityChange, handleVisibilityChange, false);
      listenersRef.current.push({ target: document, event: visibilityChange, handler: handleVisibilityChange, capture: false });
    }
  };

  const startScreenshotMonitoring = () => {
    const interval = config.screenshotProctoring.interval * 60 * 1000; // Convert to milliseconds

    const takeScreenshot = async () => {
      try {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        // Capture the current viewport
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        // Use html2canvas or similar library for better screenshot capture
        // For now, we'll create a basic canvas representation
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Add timestamp
        context.fillStyle = '#000000';
        context.font = '16px Arial';
        context.fillText(`Screenshot taken at ${new Date().toLocaleString()}`, 10, 30);

        const imageData = canvas.toDataURL(
          'image/png',
          config.screenshotProctoring.quality === 'high' ? 1.0 : 0.7
        );

        // Send screenshot to server (fire-and-forget to avoid blocking / resource exhaustion)
        axios
          .post(`/api/proctoring/session/${sessionId}/screenshot`, {
            imageData,
            flagged: false,
          })
          .then(() => {
            if (onScreenshotTaken) onScreenshotTaken(imageData);
          })
          .catch((err) => console.error('Error taking screenshot:', err));
      } catch (error) {
        console.error('Error taking screenshot:', error);
      }
    };

    const intervalId = setInterval(takeScreenshot, interval);
    screenshotIntervalRef.current = intervalId;
  };

  const startCopyPasteMonitoring = () => {
    const handleCopyPaste = (e) => {
      e.preventDefault();
      recordViolation('copy_paste', 'high', 'Copy/paste operation detected');
      return false;
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Use capture phase so we catch copy/paste/cut even when they don't bubble (e.g. on mobile)
    document.addEventListener('copy', handleCopyPaste, true);
    document.addEventListener('paste', handleCopyPaste, true);
    document.addEventListener('cut', handleCopyPaste, true);
    listenersRef.current.push(
      { target: document, event: 'copy', handler: handleCopyPaste, capture: true },
      { target: document, event: 'paste', handler: handleCopyPaste, capture: true },
      { target: document, event: 'cut', handler: handleCopyPaste, capture: true }
    );

    // Mobile fallback: many mobile browsers (especially iOS) don't fire paste on document; they do fire input with inputType 'insertFromPaste'
    const handleInput = (e) => {
      if (e.inputType === 'insertFromPaste') {
        recordViolation('copy_paste', 'high', 'Copy/paste operation detected');
      }
    };
    document.addEventListener('input', handleInput, true);
    listenersRef.current.push({ target: document, event: 'input', handler: handleInput, capture: true });

    document.addEventListener('contextmenu', handleContextMenu);
    listenersRef.current.push({ target: document, event: 'contextmenu', handler: handleContextMenu, capture: false });
  };

  const startMouseMonitoring = () => {
    let mouseLeaveCount = 0;

    const handleMouseLeave = () => {
      mouseLeaveCount++;
      if (mouseLeaveCount > config.mouseMonitoring.toleranceLimit) {
        recordViolation('mouse_leave', 'medium', 'Mouse left test area multiple times');
      } else {
        recordViolation('mouse_leave', 'low', 'Mouse left test area');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    listenersRef.current.push({ target: document, event: 'mouseleave', handler: handleMouseLeave, capture: false });
  };

  const startCameraMonitoring = async () => {
    try {
      if (config.cameraMonitoring?.enabled) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false,
        });
        setCameraStream(stream);

        // Monitor camera disconnection
        stream.getVideoTracks().forEach((track) => {
          track.onended = () => {
            recordViolation('camera_disconnect', 'high', 'Camera disconnected during test');
          };
        });
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      if (config.cameraMonitoring?.requireCamera) {
        recordViolation('camera_disconnect', 'critical', 'Failed to access required camera');
      } else {
        recordViolation('camera_disconnect', 'high', 'Failed to access camera');
      }
    }
  };

  const getToleranceLimit = useCallback(
    (type) => {
      switch (type) {
        case 'fullscreen_exit':
          return config.fullScreen?.toleranceLimit || 3;
        case 'tab_switch':
          return config.tabProctoring?.toleranceLimit || 2;
        case 'copy_paste':
          return config.copyPastePrevention?.toleranceLimit || 1;
        case 'mouse_leave':
          return config.mouseMonitoring?.toleranceLimit || 5;
        default:
          return 1;
      }
    },
    [config]
  );

  const recordViolation = useCallback(
    async (type, severity, description) => {
      try {
        // Check tolerance limits
        const currentCount = violationsRef.current[type] || 0;
        const toleranceLimit = getToleranceLimit(type);
        let finalSeverity = severity;
        let finalDescription = description;

        if (currentCount >= toleranceLimit) {
          // Exceeded tolerance limit
          finalSeverity = 'critical';
          finalDescription += ' - Tolerance limit exceeded';
        }

        // Update local violations
        setViolations((prev) => ({
          ...prev,
          [type]: currentCount + 1,
        }));

        // Send to server (throttled + fire-and-forget to avoid ERR_INSUFFICIENT_RESOURCES)
        const now = Date.now();
        const lastSent = lastEventSentByType.current[type] || 0;
        if (now - lastSent >= EVENT_THROTTLE_MS) {
          lastEventSentByType.current[type] = now;
          axios
            .post(`/api/proctoring/session/${sessionId}/event`, {
              type,
              severity: finalSeverity,
              description: finalDescription,
              metadata: {
                timestamp: new Date().toISOString(),
                userAgent: navigator.userAgent,
                url: window.location.href,
              },
            })
            .catch((err) => {
              if (!suppressToasts && err?.code === 'ERR_NETWORK') {
                toast.error(
                  t('Could not reach the server. Make sure the backend is running (port 5001).'),
                  {
                    duration: 5000,
                  }
                );
              }
            });
        }

        // Notify parent component
        if (onViolation) {
          onViolation({
            type,
            severity: finalSeverity,
            description: finalDescription,
            count: currentCount + 1,
          });
        }

        // Show warning if critical
        if (!suppressToasts) {
          if (finalSeverity === 'critical') {
            toast.error(t('Critical violation: {description}', { description: finalDescription }), { duration: 5000 });
          } else if (finalSeverity === 'high') {
            toast.error(t('High severity violation: {description}', { description: finalDescription }), { duration: 3000 });
          } else {
            toast.error(t('Violation: {description}', { description: finalDescription }), { duration: 2000 });
          }
        }
      } catch (_error) {
        // Silently handle error - violation recording is non-critical
      }
    },
    [sessionId, onViolation, getToleranceLimit, suppressToasts, t]
  );

  const cleanup = useCallback(() => {
    if (screenshotIntervalRef.current) {
      clearInterval(screenshotIntervalRef.current);
    }

    if (cameraIntervalRef.current) {
      clearInterval(cameraIntervalRef.current);
    }

    // Remove all event listeners we registered
    listenersRef.current.forEach(({ target, event, handler, capture }) => {
      try {
        target.removeEventListener(event, handler, capture);
      } catch (_) {
        // ignore
      }
    });
    listenersRef.current = [];
  }, []);

  useEffect(() => {
    if (config && sessionId) {
      initializeMonitoring();
    }
    return () => cleanup();
  }, [config, sessionId, initializeMonitoring, cleanup]);

  useEffect(() => {
    if (videoRef.current && cameraStream) {
      videoRef.current.srcObject = cameraStream;
    }
  }, [cameraStream]);

  // Camera stream lifecycle
  useEffect(() => {
    let activeStream = null;
    const startCamera = async () => {
      try {
        if (config?.cameraMonitoring?.enabled && sessionId) {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: true,
            audio: false,
          });
          activeStream = stream;
          setCameraStream(stream);

          // Monitor camera disconnection
          stream.getVideoTracks().forEach((track) => {
            track.onended = () => {
              recordViolation('camera_disconnect', 'high', 'Camera disconnected during test');
            };
          });
        }
      } catch (error) {
        console.error('Error accessing camera:', error);
        if (config?.cameraMonitoring?.requireCamera) {
          recordViolation('camera_disconnect', 'critical', 'Failed to access required camera');
        } else {
          recordViolation('camera_disconnect', 'high', 'Failed to access camera');
        }
      }
    };

    startCamera();

    return () => {
      if (activeStream) {
        activeStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [config?.cameraMonitoring?.enabled, sessionId]);

  useEffect(() => {
    let timeoutId;
    let isActive = true;

    if (cameraStream && sessionId) {
      const captureCameraFrame = async () => {
        if (!isActive) return;
        
        if (videoRef.current) {
          try {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            const videoWidth = videoRef.current.videoWidth || 320;
            const videoHeight = videoRef.current.videoHeight || 240;
            const scale = Math.min(320 / videoWidth, 1); // Cap width at 320px
            canvas.width = Math.round(videoWidth * scale);
            canvas.height = Math.round(videoHeight * scale);
            context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
            const imageData = canvas.toDataURL('image/jpeg', 0.7);

            // Fire and forget
            axios.post(`/api/proctoring/session/${sessionId}/screenshot`, {
              imageData,
              type: 'camera',
              flagged: false,
            }).catch((err) => console.error('Error uploading camera screenshot:', err));
          } catch (err) {
            console.error('Error capturing camera frame:', err);
          }
        }

        // Schedule next frame with a randomized interval between 3 and 6 seconds
        // This ensures candidates cannot predict when they are being watched
        if (isActive) {
          const nextInterval = Math.floor(Math.random() * (6000 - 3000 + 1) + 3000);
          timeoutId = setTimeout(captureCameraFrame, nextInterval);
        }
      };

      // Start the loop with a shorter initial delay
      timeoutId = setTimeout(captureCameraFrame, 1500);

      return () => {
        isActive = false;
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
  }, [cameraStream, sessionId]);

  // Video Recording Logic
  useEffect(() => {
    if (!cameraStream || !sessionId || !config?.cameraMonitoring?.enabled) return;

    try {
      // Use low bitrate to minimize memory usage and S3 costs (e.g. 50kbps)
      const options = { mimeType: 'video/webm', videoBitsPerSecond: 50000 };
      
      // Fallback if the strict options aren't supported
      let recorder;
      try {
        recorder = new MediaRecorder(cameraStream, options);
      } catch (e) {
        recorder = new MediaRecorder(cameraStream);
      }
      
      mediaRecorderRef.current = recorder;
      recordedChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
        if (blob.size > 0) {
          const formData = new FormData();
          formData.append('video', blob, 'proctoring.webm');
          // Fire-and-forget upload of the continuous proctoring video
          axios
            .post(`/api/proctoring/session/${sessionId}/video-upload`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            })
            .catch((err) => console.error('Error uploading proctoring video:', err));
        }
      };

      recorder.start(5000); // 5 second chunks
    } catch (err) {
      console.error('Failed to start MediaRecorder for proctoring:', err);
    }

    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [cameraStream, sessionId, config?.cameraMonitoring?.enabled]);

  const requestFullScreen = async () => {
    try {
      await document.documentElement.requestFullscreen();
      if (!suppressToasts) {
        toast.success(t('Full screen mode activated'));
      }
    } catch (error) {
      if (!suppressToasts) {
        toast.error(t('Failed to enter full screen mode'));
      }
    }
  };

  const exitFullScreen = async () => {
    try {
      await document.exitFullscreen();
      if (!suppressToasts) {
        toast.success(t('Exited full screen mode'));
      }
    } catch (error) {
      if (!suppressToasts) {
        toast.error(t('Failed to exit full screen mode'));
      }
    }
  };

  // When UI is disabled, keep monitoring silently without rendering anything
  if (!isActive) {
    return null;
  }

  return (
    <>
      {showUI && (
        <div className="fixed top-4 right-4 z-50">
          {/* Status Indicator */}
          <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheckIcon className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-gray-900">{t('Proctoring Active')}</span>
            </div>

            {/* Full Screen Status */}
            {config.fullScreen?.enabled && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-600">{t('Full Screen')}</span>
                <div className="flex gap-1">
                  {isFullScreen ? (
                    <button
                      onClick={exitFullScreen}
                      className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      {t('Exit')}
                    </button>
                  ) : (
                    <button
                      onClick={requestFullScreen}
                      className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                    >
                      {t('Enter')}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Violation Counts */}
            <div className="space-y-1 mb-3">
              {Object.entries(violations).map(([type, count]) => {
                if (count > 0) {
                  const toleranceLimit = getToleranceLimit(type);
                  const isExceeded = count >= toleranceLimit;

                  return (
                    <div key={type} className="flex items-center justify-between text-xs">
                      <span className="text-gray-600 capitalize">{type.replace(/_/g, ' ')}:</span>
                      <span
                        className={`font-medium ${isExceeded ? 'text-red-600' : 'text-yellow-600'}`}
                      >
                        {count}/{toleranceLimit}
                      </span>
                    </div>
                  );
                }
                return null;
              })}
            </div>

            {/* Camera Status */}
            {config.cameraMonitoring?.enabled && (
              <div className="flex items-center gap-2 text-xs text-gray-600">
                <CameraIcon className="h-4 w-4" />
                <span>{cameraStream ? t('Camera Active') : t('Camera Inactive')}</span>
              </div>
            )}

            {/* Warning Message */}
            {Object.values(violations).some((count) => count > 0) && (
              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                <div className="flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-4 w-4 text-yellow-600" />
                  <span className="text-xs text-yellow-800">
                    {t('Multiple violations detected. Please follow test guidelines.')}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {config.cameraMonitoring?.enabled && cameraStream && (
        <div className="fixed bottom-4 right-4 z-[9999] bg-black rounded-lg overflow-hidden border-2 border-white shadow-xl w-40 h-[120px] flex flex-col justify-end">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
          {/* Blinking REC indicator */}
          <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/60 px-1.5 py-0.5 rounded text-[10px] font-bold text-white tracking-wide">
            <span className="w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" />
            <span>REC</span>
          </div>
        </div>
      )}
    </>
  );
};

export default ProctoringMonitor;
